import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { Payment } from '@/api/payments'
import type { User } from '@/api/users'
import type { CashClosing, CashClosingSnapshot } from '@/api/cash-closings'

interface TypeBreakdownRow {
  name: string
  count: number
  total: number
}

interface ClosingExpenseRow {
  description: string
  amount: number
}

interface BuildClosingPdfInput {
  closing: CashClosing
  payments: Payment[]
  userMap: Map<string, User>
  typeBreakdown: TypeBreakdownRow[]
  methodLabel: (id?: string) => string
  expenses: ClosingExpenseRow[]
}

interface BuildActaPdfInput {
  closing: CashClosing
  snapshot: CashClosingSnapshot
}

interface MonthlyClosingRow {
  closing_number: number
  date_from: string
  date_to: string
  responsible: string
  status: 'draft' | 'received' | 'voided'
  grand_total: number
}

interface MonthlyReconciliation {
  totalOpening: number
  totalExpenses: number
  totalCounted: number
  totalExpected: number
  totalDifference: number
  reconciledCount: number
}

interface BuildMonthlyPdfInput {
  month: string
  payments: Payment[]
  userMap: Map<string, User>
  typeBreakdown: TypeBreakdownRow[]
  methodLabel: (id?: string) => string
  methodTotals: { cash: number; card: number; transfer: number; other: number }
  closings: MonthlyClosingRow[]
  reconciliation: MonthlyReconciliation
}

const PAGE_LEFT = 14
const PAGE_RIGHT = 196

function formatCOP(n: number) {
  return `$${Number(n).toLocaleString('es-CO')}`
}

function formatDate(s: string) {
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatMonthLabel(month: string) {
  const label = new Date(`${month}-02T00:00:00`).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function statusLabel(s: string) {
  const map: Record<string, string> = { paid: 'Pagado', pending: 'Pendiente', cancelled: 'Cancelado', refunded: 'Reembolsado' }
  return map[s] ?? s.charAt(0).toUpperCase() + s.slice(1)
}

function lastTableY(doc: jsPDF) {
  // @ts-expect-error - lastAutoTable is attached by the plugin at runtime
  return doc.lastAutoTable.finalY as number
}

function divider(doc: jsPDF, y: number) {
  doc.setDrawColor(225)
  doc.setLineWidth(0.3)
  doc.line(PAGE_LEFT, y, PAGE_RIGHT, y)
  return y + 7
}

function sectionTitle(doc: jsPDF, text: string, y: number) {
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20)
  doc.text(text, PAGE_LEFT, y)
  doc.setDrawColor(16, 185, 129)
  doc.setLineWidth(0.7)
  doc.line(PAGE_LEFT, y + 1.8, PAGE_LEFT + 16, y + 1.8)
  return y + 9
}

function bigNumber(doc: jsPDF, label: string, value: string, caption: string, y: number) {
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(130)
  doc.text(label.toUpperCase(), PAGE_LEFT, y)
  doc.setFontSize(25)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(13, 148, 110)
  doc.text(value, PAGE_LEFT, y + 10)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(130)
  doc.text(caption, PAGE_LEFT, y + 16)
  return y + 24
}

function kv(doc: jsPDF, label: string, value: string, y: number, opts: { bold?: boolean; color?: [number, number, number] } = {}) {
  doc.setFontSize(10.5)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(95)
  doc.text(label, PAGE_LEFT, y)
  doc.setFont('helvetica', opts.bold ? 'bold' : 'normal')
  const c = opts.color ?? [25, 25, 25]
  doc.setTextColor(c[0], c[1], c[2])
  doc.text(value, PAGE_RIGHT, y, { align: 'right' })
  return y + 7
}

export function buildCashClosingReportPdf({ closing, payments, userMap, typeBreakdown, methodLabel, expenses }: BuildClosingPdfInput) {
  const doc = new jsPDF()
  const generatedAt = new Date().toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' })
  const periodLabel = `${formatDate(closing.date_from)} → ${formatDate(closing.date_to)}`
  const statusLabelMap = { draft: 'Borrador', received: 'Recibida', voided: 'Anulada' } as const

  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20)
  doc.text(`Reporte de Cierre N° ${closing.closing_number}`, PAGE_LEFT, 20)

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(130)
  doc.text(`${periodLabel}  ·  ${statusLabelMap[closing.status]}`, PAGE_LEFT, 27)
  doc.text(`Cajero: ${closing.responsible}  ·  Generado el ${generatedAt}`, PAGE_LEFT, 33)

  let y = divider(doc, 40)
  y = bigNumber(doc, 'Total de ventas', formatCOP(Number(closing.grand_total)), `${closing.payment_count} cobro${closing.payment_count !== 1 ? 's' : ''}`, y)
  y += 3

  const methodRows = (
    [
      ['Efectivo', Number(closing.total_cash)],
      ['Tarjeta', Number(closing.total_card)],
      ['Transferencia', Number(closing.total_transfer)],
      ['Otros', Number(closing.total_other)],
    ] as [string, number][]
  ).filter(([, v]) => v > 0)

  if (methodRows.length > 0) {
    y = sectionTitle(doc, 'Totales por método', y)
    for (const [label, value] of methodRows) {
      y = kv(doc, label, formatCOP(value), y)
    }
    y += 5
  }

  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount), 0)
  const expectedCash = Number(closing.opening_cash || 0) + Number(closing.total_cash) - totalExpenses
  const hasReconciliation = Number(closing.opening_cash || 0) > 0 || totalExpenses > 0 || closing.counted_cash != null

  if (hasReconciliation) {
    y = sectionTitle(doc, 'Reconciliación de efectivo', y)
    if (Number(closing.opening_cash || 0) > 0) {
      y = kv(doc, 'Fondo inicial', formatCOP(Number(closing.opening_cash)), y)
    }
    if (totalExpenses > 0) {
      y = kv(doc, 'Gastos de caja', `-${formatCOP(totalExpenses)}`, y, { color: [180, 95, 9] })
      autoTable(doc, {
        startY: y - 2,
        body: expenses.map(e => [e.description, `-${formatCOP(Number(e.amount))}`]),
        theme: 'plain',
        styles: { fontSize: 9, textColor: [130, 130, 130], cellPadding: 1.2 },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: 20, right: 14 },
      })
      y = lastTableY(doc) + 5
    }
    y = kv(doc, 'Efectivo esperado', formatCOP(expectedCash), y, { bold: true })
    if (closing.counted_cash != null) {
      y = kv(doc, 'Efectivo contado', formatCOP(Number(closing.counted_cash)), y)
      const diff = Number(closing.counted_cash) - expectedCash
      if (Math.abs(diff) < 0.01) {
        y = kv(doc, 'Diferencia', 'Caja exacta', y, { bold: true, color: [16, 150, 80] })
      } else {
        y = kv(doc, diff > 0 ? 'Sobrante' : 'Faltante', formatCOP(Math.abs(diff)), y, { bold: true, color: diff > 0 ? [180, 130, 9] : [200, 50, 50] })
      }
    }
    if (closing.received_by) {
      y = kv(doc, 'Administrador que recibe', closing.received_by, y)
    }
    y += 5
  }

  const nonZeroTypes = typeBreakdown.filter(t => t.total > 0)
  if (nonZeroTypes.length > 0) {
    y = sectionTitle(doc, 'Ventas por tipo', y)
    autoTable(doc, {
      startY: y,
      head: [['Tipo', 'Cantidad', 'Total']],
      body: nonZeroTypes.map(t => [t.name, String(t.count), formatCOP(t.total)]),
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 9.5, cellPadding: 3.5 },
      margin: { left: PAGE_LEFT, right: 14 },
    })
    y = lastTableY(doc) + 12
  }

  y = sectionTitle(doc, 'Detalle de transacciones', y)
  autoTable(doc, {
    startY: y,
    head: [['Miembro', 'Fecha', 'Monto', 'Método', 'Estado']],
    body: payments.map(p => {
      const user = p.user_id ? userMap.get(p.user_id) : undefined
      const name = user?.name ?? p.client_name ?? '—'
      return [name, formatDate(p.paid_at), formatCOP(Number(p.amount)), methodLabel(p.payment_method_id), statusLabel(p.status)]
    }),
    theme: 'striped',
    headStyles: { fillColor: [16, 185, 129] },
    styles: { fontSize: 9.5, cellPadding: 3.5 },
    margin: { left: PAGE_LEFT, right: 14 },
  })

  if (closing.notes) {
    y = lastTableY(doc) + 8
    doc.setFontSize(9)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(130)
    doc.text(`Notas: ${closing.notes}`, PAGE_LEFT, y)
  }

  doc.save(`reporte_cierre_${closing.closing_number}.pdf`)
}

export function buildHandoverActaPdf({ closing, snapshot }: BuildActaPdfInput) {
  const doc = new jsPDF()
  const periodLabel = `${formatDate(closing.date_from)} → ${formatDate(closing.date_to)}`
  const totalExpenses = snapshot.expenses.reduce((s, e) => s + Number(e.amount), 0)
  const expectedCash = Number(closing.opening_cash || 0) + Number(closing.total_cash) - totalExpenses
  const countedCash = closing.counted_cash != null ? Number(closing.counted_cash) : null
  const diff = countedCash != null ? countedCash - expectedCash : null

  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20)
  doc.text('Acta de Entrega de Caja', PAGE_LEFT, 20)

  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(90)
  doc.text(`Cierre N° ${closing.closing_number}`, PAGE_LEFT, 28)

  if (closing.status === 'voided') {
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(200, 50, 50)
    doc.text('ANULADA', PAGE_RIGHT, 28, { align: 'right' })
  }

  let y = divider(doc, 35)

  y = kv(doc, 'Período del turno', periodLabel, y)
  y = kv(doc, 'Cajero', closing.responsible, y)
  y = kv(doc, 'Administrador que recibe', closing.received_by ?? '—', y)
  if (closing.received_at) {
    y = kv(doc, 'Fecha y hora de entrega', new Date(closing.received_at).toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' }), y)
  }
  y += 5

  y = sectionTitle(doc, 'Entrega de efectivo', y)
  y = kv(doc, 'Efectivo esperado', formatCOP(expectedCash), y)
  y = kv(doc, 'Total entregado (contado)', countedCash != null ? formatCOP(countedCash) : '—', y, { bold: true })
  if (diff != null) {
    if (Math.abs(diff) < 0.01) {
      y = kv(doc, 'Diferencia', 'Caja exacta', y, { bold: true, color: [16, 150, 80] })
    } else {
      y = kv(doc, diff > 0 ? 'Sobrante' : 'Faltante', formatCOP(Math.abs(diff)), y, { bold: true, color: diff > 0 ? [180, 130, 9] : [200, 50, 50] })
    }
  }
  y += 10

  doc.setFontSize(10.5)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20)
  doc.text('Observaciones', PAGE_LEFT, y)
  y += 6
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(90)
  const notesText = closing.notes?.trim() || 'Sin observaciones.'
  const wrapped = doc.splitTextToSize(notesText, PAGE_RIGHT - PAGE_LEFT)
  doc.text(wrapped, PAGE_LEFT, y)
  y += wrapped.length * 5 + 10

  if (closing.status === 'voided' && closing.void_reason) {
    doc.setFontSize(9.5)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(170, 60, 60)
    doc.text(`Motivo de anulación: ${closing.void_reason}`, PAGE_LEFT, y)
    y += 12
  }

  // Firmas
  const sigY = Math.max(y + 15, 240)
  doc.setDrawColor(150)
  doc.setLineWidth(0.3)
  doc.line(PAGE_LEFT, sigY, PAGE_LEFT + 75, sigY)
  doc.line(PAGE_RIGHT - 75, sigY, PAGE_RIGHT, sigY)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(110)
  doc.text('Firma Cajero', PAGE_LEFT, sigY + 5)
  doc.text(closing.responsible, PAGE_LEFT, sigY + 10)
  doc.text('Firma Administrador', PAGE_RIGHT - 75, sigY + 5)
  doc.text(closing.received_by ?? '—', PAGE_RIGHT - 75, sigY + 10)

  doc.setFontSize(8)
  doc.setTextColor(160)
  doc.text(`Generado el ${new Date().toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' })} · Snapshot capturado el ${new Date(snapshot.capturedAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}`, PAGE_LEFT, 285)

  doc.save(`acta_entrega_cierre_${closing.closing_number}.pdf`)
}

export function buildMonthlyConsolidationPdf({ month, payments, userMap, typeBreakdown, methodLabel, methodTotals, closings, reconciliation }: BuildMonthlyPdfInput) {
  const doc = new jsPDF()
  const generatedAt = new Date().toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' })
  const monthLabel = formatMonthLabel(month)
  const grandTotal = payments.reduce((s, p) => s + Number(p.amount), 0)

  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(20)
  doc.text('Consolidado Mensual', PAGE_LEFT, 20)

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(130)
  doc.text(`${monthLabel}  ·  Generado el ${generatedAt}`, PAGE_LEFT, 27)

  let y = divider(doc, 35)
  y = bigNumber(doc, 'Total de ventas del mes', formatCOP(grandTotal), `${payments.length} cobro${payments.length !== 1 ? 's' : ''}`, y)
  y += 3

  const methodRows = (
    [
      ['Efectivo', methodTotals.cash],
      ['Tarjeta', methodTotals.card],
      ['Transferencia', methodTotals.transfer],
      ['Otros', methodTotals.other],
    ] as [string, number][]
  ).filter(([, v]) => v > 0)

  if (methodRows.length > 0) {
    y = sectionTitle(doc, 'Totales por método', y)
    for (const [label, value] of methodRows) {
      y = kv(doc, label, formatCOP(value), y)
    }
    y += 5
  }

  if (reconciliation.reconciledCount > 0) {
    y = sectionTitle(doc, 'Reconciliación de caja', y)
    if (reconciliation.totalOpening > 0) {
      y = kv(doc, 'Fondo inicial total', formatCOP(reconciliation.totalOpening), y)
    }
    if (reconciliation.totalExpenses > 0) {
      y = kv(doc, 'Gastos de caja totales', `-${formatCOP(reconciliation.totalExpenses)}`, y, { color: [180, 95, 9] })
    }
    y = kv(doc, 'Efectivo esperado total', formatCOP(reconciliation.totalExpected), y, { bold: true })
    y = kv(doc, 'Efectivo contado total', formatCOP(reconciliation.totalCounted), y)
    if (Math.abs(reconciliation.totalDifference) < 0.01) {
      y = kv(doc, 'Diferencia', 'Caja exacta', y, { bold: true, color: [16, 150, 80] })
    } else {
      y = kv(
        doc,
        reconciliation.totalDifference > 0 ? 'Sobrante total' : 'Faltante total',
        formatCOP(Math.abs(reconciliation.totalDifference)),
        y,
        { bold: true, color: reconciliation.totalDifference > 0 ? [180, 130, 9] : [200, 50, 50] },
      )
    }
    y += 5
  }

  const nonZeroTypes = typeBreakdown.filter(t => t.total > 0)
  if (nonZeroTypes.length > 0) {
    y = sectionTitle(doc, 'Ventas por tipo', y)
    autoTable(doc, {
      startY: y,
      head: [['Tipo', 'Cantidad', 'Total']],
      body: nonZeroTypes.map(t => [t.name, String(t.count), formatCOP(t.total)]),
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 9.5, cellPadding: 3.5 },
      margin: { left: PAGE_LEFT, right: 14 },
    })
    y = lastTableY(doc) + 12
  }

  if (closings.length > 0) {
    y = sectionTitle(doc, 'Cierres de caja del mes', y)
    autoTable(doc, {
      startY: y,
      head: [['N°', 'Período', 'Cajero', 'Estado', 'Total']],
      body: closings.map(c => [
        String(c.closing_number),
        `${formatDate(c.date_from)} → ${formatDate(c.date_to)}`,
        c.responsible,
        c.status === 'received' ? 'Recibida' : c.status === 'voided' ? 'Anulada' : 'Borrador',
        formatCOP(Number(c.grand_total)),
      ]),
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 9.5, cellPadding: 3.5 },
      margin: { left: PAGE_LEFT, right: 14 },
    })
    y = lastTableY(doc) + 12
  }

  y = sectionTitle(doc, 'Detalle de transacciones', y)
  autoTable(doc, {
    startY: y,
    head: [['Miembro', 'Fecha', 'Monto', 'Método', 'Estado']],
    body: payments.map(p => {
      const user = p.user_id ? userMap.get(p.user_id) : undefined
      const name = user?.name ?? p.client_name ?? '—'
      return [name, formatDate(p.paid_at), formatCOP(Number(p.amount)), methodLabel(p.payment_method_id), statusLabel(p.status)]
    }),
    theme: 'striped',
    headStyles: { fillColor: [16, 185, 129] },
    styles: { fontSize: 9.5, cellPadding: 3.5 },
    margin: { left: PAGE_LEFT, right: 14 },
  })

  doc.save(`consolidado_${month}.pdf`)
}
